import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Package, Shield, TrendingUp, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { getCategories } from '../services/api';
import { companyInfo, services } from '../mockData';

// Hero carousel slides data
const heroSlides = [
  {
    image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&w=1920&q=80',
    title: 'Premium Indian Spices',
    subtitle: 'MDH, Everest, Badshah & More',
    description: 'Export-quality turmeric, chili, cumin, cardamom and masala blends from India\'s trusted brands.'
  },
  {
    image: 'https://images.unsplash.com/photo-1586201375761-83865001e31c?auto=format&fit=crop&w=1920&q=80',
    title: 'Basmati Rice & Grains',
    subtitle: 'India Gate, Kohinoor, Daawat',
    description: 'Premium basmati rice, pulses, and grains from India\'s finest producers for global markets.'
  },
  {
    image: 'https://images.unsplash.com/photo-1499636136210-6f4ee915583e?auto=format&fit=crop&w=1920&q=80',
    title: 'Snacks & Biscuits',
    subtitle: 'Haldirams, Britannia, Parle',
    description: 'Namkeen, cookies, biscuits and savory snacks loved by millions across the world.'
  },
  {
    image: 'https://images.unsplash.com/photo-1544787219-7f47ccb76574?auto=format&fit=crop&w=1920&q=80',
    title: 'Tea, Coffee & Beverages',
    subtitle: 'Tata, Brooke Bond, Nescafe',
    description: 'Premium Indian teas, coffees, and health drinks for every household.'
  }
];

const Home = () => {
  const [productCategories, setProductCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const data = await getCategories();
        setProductCategories(data);
      } catch (err) {
        console.error('Error loading categories:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const featuredProducts = productCategories.slice(0, 6);
  const featuredServices = services.slice(0, 4);

  return (
    <div className="min-h-screen">
      {/* Hero Carousel Section */}
      <section className="hero-carousel" data-testid="hero-carousel">
        {/* Slides */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            style={{ backgroundImage: `url(${slide.image})` }}
          >
            <div className="hero-overlay"></div>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 hero-content">
              <div className="max-w-3xl">
                <p className="text-lg md:text-xl mb-2 text-[var(--gold-light)] font-medium animate-slide-up">
                  {slide.subtitle}
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 animate-slide-up-delay">
                  {slide.title}
                </h1>
                <div className="decorative-line max-w-md !my-4"></div>
                <p className="text-base md:text-lg mb-8 text-gray-100 animate-slide-up-delay-2">
                  {slide.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 animate-slide-up-delay-3">
                  <Link to="/products">
                    <Button className="btn-gold text-lg px-8 py-6" data-testid="explore-products-btn">
                      Explore Products
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button variant="outline" className="text-lg px-8 py-6 bg-white/10 backdrop-blur-sm text-white border-white hover:bg-white hover:text-[var(--primary-teal)]" data-testid="request-quote-btn">
                      Request Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="carousel-arrow carousel-arrow-left"
          aria-label="Previous slide"
          data-testid="carousel-prev"
        >
          <ChevronLeft size={28} />
        </button>
        <button
          onClick={nextSlide}
          className="carousel-arrow carousel-arrow-right"
          aria-label="Next slide"
          data-testid="carousel-next"
        >
          <ChevronRight size={28} />
        </button>

        {/* Dots Navigation */}
        <div className="carousel-dots" data-testid="carousel-dots">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 section-teal">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="animate-fade-in-up">
              <div className="text-4xl font-bold mb-2" style={{ color: 'var(--gold-accent)' }}>1600+</div>
              <p className="text-white">Products</p>
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="text-4xl font-bold mb-2" style={{ color: 'var(--gold-accent)' }}>10+</div>
              <p className="text-white">Countries Served</p>
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="text-4xl font-bold mb-2" style={{ color: 'var(--gold-accent)' }}>220+</div>
              <p className="text-white">Trusted Brands</p>
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="text-4xl font-bold mb-2" style={{ color: 'var(--gold-accent)' }}>8+</div>
              <p className="text-white">Years Industry Experience</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6" style={{ color: 'var(--primary-teal)' }}>
                Who We Are
              </h2>
              <div className="decorative-line max-w-xs mb-6"></div>
              <p className="text-lg mb-6" style={{ color: 'var(--text-medium)' }}>
                {companyInfo.description}
              </p>
              <p className="text-base mb-8" style={{ color: 'var(--text-medium)' }}>
                {companyInfo.mission}
              </p>
              <Link to="/about">
                <Button className="btn-teal">
                  Learn More About Us
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1596040033229-a9821ebd058d?auto=format&fit=crop&w=800&q=80"
                alt="Premium Indian Spices"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[var(--gold-accent)] text-white p-6 rounded-lg shadow-xl">
                <Globe size={32} className="mb-2" />
                <p className="text-sm font-semibold">Global Reach</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 section-cream pattern-overlay">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--primary-teal)' }}>
              Our Product Range
            </h2>
            <div className="decorative-line max-w-md"></div>
            <p className="text-lg mt-4" style={{ color: 'var(--text-medium)' }}>
              Discover our extensive catalog of premium quality products
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-lg" style={{ color: 'var(--text-medium)' }}>Loading products...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-lg text-red-600">{error}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {featuredProducts.map((category) => {
              const totalBrands = category.brands.length;
              const topBrands = category.brands.slice(0, 3);
              return (
                <Card key={category.id} className="card-hover overflow-hidden border-0 shadow-lg">
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={category.image} 
                      alt={category.name}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                    />
                    <div className="category-card-overlay"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h3 className="text-xl font-bold mb-2">{category.name}</h3>
                      <p className="text-sm text-gray-200">{category.description}</p>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex flex-wrap gap-2">
                      {topBrands.map((brand, idx) => (
                        <span 
                          key={idx} 
                          className="text-xs px-3 py-1 rounded-full"
                          style={{ 
                            backgroundColor: 'var(--cream-bg)',
                            color: 'var(--primary-teal)'
                          }}
                        >
                          {brand.name}
                        </span>
                      ))}
                      {totalBrands > 3 && (
                        <span 
                          className="text-xs px-3 py-1 rounded-full"
                          style={{ 
                            backgroundColor: 'var(--gold-accent)',
                            color: 'white'
                          }}
                        >
                          +{totalBrands - 3} more
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
              </div>

              <div className="text-center">
                <Link to="/products">
                  <Button className="btn-gold text-lg px-8 py-6">
                    View All Products
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--primary-teal)' }}>
              Our Services
            </h2>
            <div className="decorative-line max-w-md"></div>
            <p className="text-lg mt-4" style={{ color: 'var(--text-medium)' }}>
              Comprehensive solutions for your export needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {featuredServices.map((service) => (
              <Card key={service.id} className="card-hover border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl" style={{ color: 'var(--primary-teal)' }}>
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-base mt-2">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <Shield size={16} style={{ color: 'var(--gold-accent)' }} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="btn-teal text-lg px-8 py-6">
                Explore All Services
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 section-teal">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">
              Why Choose Triton Global Trade?
            </h2>
            <div className="decorative-line max-w-md"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyInfo.whyChooseUs.map((item, idx) => (
              <div key={idx} className="text-center p-6 rounded-lg bg-white/10 backdrop-blur-sm card-hover">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--gold-accent)] flex items-center justify-center">
                  {idx === 0 && <Package size={32} className="text-white" />}
                  {idx === 1 && <Shield size={32} className="text-white" />}
                  {idx === 2 && <TrendingUp size={32} className="text-white" />}
                  {idx === 3 && <Star size={32} className="text-white" />}
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">{item.title}</h3>
                <p className="text-gray-200">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 section-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="rounded-2xl p-12 text-center text-white"
            style={{
              background: 'linear-gradient(135deg, var(--primary-teal) 0%, var(--primary-teal-dark) 100%)'
            }}
          >
            <h2 className="text-4xl font-bold mb-4">
              Ready to Partner With Us?
            </h2>
            <p className="text-xl mb-8 text-gray-100">
              Let's discuss how we can fulfill your sourcing requirements
            </p>
            <Link to="/contact">
              <Button className="btn-gold text-lg px-10 py-6">
                Get Started Today
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
